// src/pages/Dashboard.tsx
import React, { useState, useEffect } from "react";
import { StatsCard } from "../components/dashboard/StatsCard";
import { RecentActivity } from "../components/dashboard/RecentActivity";
import {
  DollarSign,
  Users,
  Calendar,
  TrendingUp,
  Clock,
  Hotel,
} from "lucide-react";
import { bookingsApi } from "../api";
import { Link } from "react-router-dom";

interface DashboardStats {
  total: number;
  pending: number;
  confirmed: number;
  completed: number;
  cancelled: number;
  revenue: number;
  airportTransfers?: number;
  hotelChecks?: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await bookingsApi.getStats();
        setStats(response.data.data);
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      title: "Total Revenue",
      value: `€${stats?.revenue?.toLocaleString() || "0"}`,
      icon: DollarSign,
      color: "bg-green-500",
      change: "+12.5%",
    },
    {
      title: "Total Bookings",
      value: stats?.total?.toString() || "0",
      icon: Calendar,
      color: "bg-blue-500",
      change: "+8.2%",
    },
    {
      title: "Airport Transfers",
      value: stats?.airportTransfers?.toString() || "0",
      icon: Calendar,
      color: "bg-indigo-600",
      change: "Active rides",
    },
    {
      title: "Hotel Checks",
      value: stats?.hotelChecks?.toString() || "0",
      icon: Hotel,
      color: "bg-purple-600",
      change: "Inquiries",
    },
    {
      title: "Pending Bookings",
      value: stats?.pending?.toString() || "0",
      icon: Clock,
      color: "bg-yellow-500",
      change: "-2.4%",
    },
    {
      title: "Confirmed",
      value: stats?.confirmed?.toString() || "0",
      icon: Hotel,
      color: "bg-emerald-500",
      change: "+3.1%",
    },
    {
      title: "Completed",
      value: stats?.completed?.toString() || "0",
      icon: Users,
      color: "bg-indigo-500",
      change: "+5.7%",
    },
    {
      title: "Cancelled",
      value: stats?.cancelled?.toString() || "0",
      icon: TrendingUp,
      color: "bg-red-500",
      change: "+1.3%",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-500">
          Welcome back! Here's what's happening with your business.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statCards.map((card, index) => (
          <StatsCard
            key={index}
            title={card.title}
            value={card.value}
            icon={card.icon}
            color={card.color}
            change={card.change}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
            <RecentActivity />
          </div>
        </div>
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <Link to={"/bookings"}>
                <button className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                  View All Bookings
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
